import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { Tag, TagModule } from 'primeng/tag';
import { ShSeverity, ShSize } from '../models';

type PrimeTagSeverity = Tag['severity'];

/** Maps SHIPM8 severities to PrimeNG tag severities (`primary` is PrimeNG's default). */
export function tagSeverity(severity: ShSeverity): PrimeTagSeverity {
  switch (severity) {
    case 'primary':
      return undefined;
    case 'warning':
      return 'warn';
    case 'neutral':
      return 'contrast';
    default:
      return severity;
  }
}

/**
 * A small coloured label: a status, a count, a category.
 *
 *   <sh-tag label="shared.statuses.active" severity="success" />
 *   <sh-tag [label]="'' + count" severity="danger" size="sm" />
 *
 * `label` is a translation key or plain text. `size` only sets the central
 * `sh-tag-sm` / `sh-tag-lg` class; md is PrimeNG's own size.
 */
@Component({
  selector: 'sh-tag',
  changeDetection: ChangeDetectionStrategy.Default,
  imports: [TranslateModule, TagModule],
  template: `
    <p-tag
      [severity]="primeSeverity()"
      [value]="label() | translate"
      [icon]="icon()"
      [rounded]="rounded()"
      [styleClass]="sizeClass()"
    ></p-tag>
  `,
})
export class ShTagComponent {
  /** Translation key or plain text. */
  readonly label = input<string>();
  readonly severity = input<ShSeverity>('primary');
  readonly size = input<ShSize>('md');
  /** Font Awesome classes. */
  readonly icon = input<string>();
  readonly rounded = input(false, { transform: booleanAttribute });

  protected readonly primeSeverity = computed(() =>
    tagSeverity(this.severity())
  );
  protected readonly sizeClass = computed(() =>
    this.size() === 'md' ? undefined : `sh-tag-${this.size()}`
  );
}
